import { useEffect, useState } from 'react';
import {
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { listBackups, downloadBackup, BackupEntry } from '../api/backups';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatSize(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

const TYPE_LABELS: Record<string, string> = {
  pre_delete: 'Pre-delete',
  pre_migration: 'Pre-migration',
  manual: 'Manual',
  scheduled: 'Scheduled',
  file: 'File',
};

function typeBadge(backupType: string): { label: string; className: string } {
  const label = TYPE_LABELS[backupType] ?? backupType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const classMap: Record<string, string> = {
    pre_delete: 'bg-red-100 text-red-700',
    pre_migration: 'bg-blue-100 text-blue-700',
    manual: 'bg-slate-100 text-slate-700',
    scheduled: 'bg-green-100 text-green-700',
    file: 'bg-slate-100 text-slate-600',
  };

  return {
    label,
    className: classMap[backupType] ?? 'bg-slate-100 text-slate-600',
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

/** `embedded` renders this inside the Database section's Backups tab, which
 *  already supplies the page frame — so the standalone header is dropped and
 *  the outer padding comes from the tab canvas instead. */
export default function Backups({ embedded = false }: { embedded?: boolean }) {
  const [backups, setBackups] = useState<BackupEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function fetchBackups() {
    setLoading(true);
    setError(null);
    try {
      const res = await listBackups();
      setBackups(res.data.backups);
    } catch {
      setError('Failed to load backups.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchBackups();
  }, []);

  async function handleDownload(entry: BackupEntry) {
    if (downloadingId || !entry.file_name) return;
    setDownloadingId(entry.id);
    try {
      await downloadBackup(entry.file_name);
    } catch {
      window.alert(`Download failed for ${entry.file_name}`);
    } finally {
      setDownloadingId(null);
    }
  }

  const filtered = backups.filter((b) => {
    const term = searchTerm.toLowerCase();
    if (
      term &&
      !b.company_name?.toLowerCase().includes(term) &&
      !b.company_code?.toLowerCase().includes(term)
    )
      return false;
    if (typeFilter && b.backup_type !== typeFilter) return false;
    return true;
  });

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      {/* Page header */}
      <div className="flex items-center justify-between">
        {embedded ? <div /> : (
          <div className="flex items-center gap-3">
            <ArchiveBoxIcon className="w-7 h-7 text-slate-600" />
            <h1 className="text-2xl font-bold text-slate-900">Backups</h1>
          </div>
        )}
        <button
          onClick={fetchBackups}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Warning banner */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">
        <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <span>
          Backups live in the server's backup volume. If it isn't a persistent/mounted volume
          they're cleared on redeploy — download anything you need to keep.
        </span>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Search by company…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Types</option>
          <option value="pre_delete">Pre-delete</option>
          <option value="pre_migration">Pre-migration</option>
          <option value="manual">Manual</option>
          <option value="scheduled">Scheduled</option>
          <option value="file">File</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500 text-sm">
          <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={fetchBackups}
            className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
          No backups found.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 font-semibold text-slate-600">Company</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Type</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Created</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Size</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Download</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((entry) => {
                const badge = typeBadge(entry.backup_type);
                const isDownloading = downloadingId === entry.id;
                return (
                  <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                    {/* Company */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {entry.company_name ?? entry.company_code ?? entry.file_name}
                      </div>
                      {entry.company_name && entry.company_code && (
                        <div className="text-xs text-slate-500">{entry.company_code}</div>
                      )}
                    </td>
                    {/* Type badge */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </td>
                    {/* Created */}
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {formatDate(entry.created_at)}
                    </td>
                    {/* Size */}
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {formatSize(entry.size_bytes)}
                    </td>
                    {/* Download */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDownload(entry)}
                        disabled={!entry.downloadable || !entry.file_name || !!downloadingId}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isDownloading ? (
                          <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                        )}
                        {isDownloading ? 'Downloading…' : 'Download'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
